import { createTestArena, TEST_ARENA } from './testArena.js';
import { createSubwayArena, SUBWAY_ARENA } from './subwayArena.js';
import { createRooftopArena, ROOFTOP_ARENA } from './rooftopArena.js';
import { createGarageArena, GARAGE_ARENA } from './garageArena.js';
import { createClubArena, CLUB_ARENA } from './clubArena.js';
import { inPit } from '../core/pit.js';

/**
 * Which arenas exist, and how to put one on screen.
 *
 * Until now `main.js` imported one arena directly and everything downstream was
 * built around whatever that one happened to declare — the walkable floor, the
 * marks, the camera, the floor and the lights all got wired in by hand at boot.
 * That works exactly as long as there is one arena.
 *
 * So an arena is a **contract** now, and every one of them answers the same
 * shape:
 *
 *   surfaces   the hard faces, as `surfaces.js` reads them
 *   bounds     where a fighter may walk
 *   spawns     where the two of them start a round
 *   camera     framing overrides: the side it is watched from, how far back
 *   pit        a hole in the floor, or null
 *   ground     false when it brings its own, so the stage's grid goes away
 *   lit        true when it brings its own, so the stage's key light does too
 *   crowd      the spectators, or null in an arena nobody came to watch
 *   signature  the one finish this arena has and no other does: a pit, or the
 *              ids of the surfaces it is done against
 *   update(dt) anything in it that moves on its own
 *   onStruck   optional: a hard surface of its own was hit, and where. An arena
 *              that shows damage listens; one that does not leaves it out. It
 *              may answer `{ rest: {x, z} }` — where a body it has just
 *              finished comes to rest, which is how a fighter ends up *in* a
 *              broken bar rather than bounced off the front of one
 *   reset()    optional: back to how it was before the round, the way the
 *              fighters are put back on their marks
 *
 * Most arenas answer "nothing in particular" to most of it, which is the point
 * of having a default. `testArena` is a room with four identical walls; it has
 * no opinion about the camera and no hole to fall down.
 */

export const ARENAS = Object.freeze([
  Object.freeze({ id: 'subway', name: SUBWAY_ARENA.name, create: createSubwayArena }),
  Object.freeze({ id: 'rooftop', name: ROOFTOP_ARENA.name, create: createRooftopArena }),
  Object.freeze({ id: 'garage', name: GARAGE_ARENA.name, create: createGarageArena }),
  Object.freeze({ id: 'club', name: CLUB_ARENA.name, create: createClubArena }),
  // Not a place: nothing on the circuit is fought in it, and `places()` and the
  // venue check leave it out. Four walls for testing what a wall does.
  Object.freeze({ id: 'testArena', name: TEST_ARENA.name, create: createTestArena, place: false }),
]);

export const DEFAULT_ARENA = 'subway';

/**
 * The two things the host does to a body on an arena's behalf, both after the
 * rules are finished with it: lay it where the arena says a finish leaves it,
 * and let it fall when the floor under it is a hole.
 */
export const SETTLE = Object.freeze({
  /** Seconds to let the rebound off the surface play out first. */
  wait: 0.2,
  /** Seconds to ease into the resting place, rather than a teleport. */
  move: 0.3,
  /** Metres per second per second. A fall that reads as one. */
  gravity: 9.8,
});

/**
 * What a finish against part of an arena is called, in words: "a car", "the
 * parapet". The id a hit carries is a key, the same way a move's is (gotcha
 * 38), and the name is the arena's data. Null when the arena does not know it.
 */
export function finishName(arena, id) {
  if (!arena || !id) return null;
  if (arena.pit?.id === id) return arena.pit.name ?? null;
  return arena.surfaces?.find((surface) => surface.id === id)?.name ?? null;
}

/**
 * Whether a train is **over** a spot on the track — not merely in its passing
 * phase (step 97).
 *
 * `train.present` is true for the whole crossing, which runs 11 m into the
 * tunnel at each end so the train is out of sight before it turns round: about
 * 1.2 s at each end in which it is "in the station" and nowhere near anybody.
 * The pit used to be lethal for all of it, and the finish the game called "the
 * train" was, more often than not, a body falling onto an empty track after
 * the train had gone — the first filmed takes of it showed exactly that. Now
 * the body has to go in where the train is, or where its nose will be in the
 * next `reach` metres.
 */
export const TRAIN_REACH = 1.0;
export function trainOver(train, at, reach = TRAIN_REACH) {
  if (!train?.present) return false;
  if (!at) return true;
  const { center, halfX } = train.surface;
  return Math.abs(at.x - center.x) <= halfX + reach;
}

export const arenaIds = () => ARENAS.map((entry) => entry.id);
/** The arenas a card can be fought in: everything registered but the test room. */
export const places = () => ARENAS.filter((entry) => entry.place !== false).map((entry) => entry.id);
export const findArena = (id) => ARENAS.find((entry) => entry.id === id) ?? null;

/**
 * Holds the arena that is currently loaded, and swaps it.
 *
 * Everything an arena decides for somebody else is re-pointed here, in one
 * place, because the failure mode of doing it at the call sites is a swap that
 * gets four of the five right — a fighter walking the old arena's rectangle
 * around the new one, or a pit tested against a platform that is not there.
 *
 * `locomotors` are the things with legs to re-floor — the player's control layer
 * and the AI — and, for the ones that read a room, eyes to re-point. They are
 * passed rather than reached for, because this module has no business knowing
 * what a control layer is.
 */
export function createArenaHost({
  stage,
  fight,
  match,
  locomotors = [],
  /**
   * The crowd's mood, as a rule object. Passed *into* each arena rather than
   * built by one: where people stand is the arena's business, how loud they are
   * is a function of the fighters, and an arena has no opinion about who is
   * winning. An arena nobody came to watch simply ignores it.
   */
  crowd = null,
  onWarn,
  onChange,
} = {}) {
  let current = null;
  /**
   * An arena somebody chose by hand — the overlay's buttons, a harness's
   * `FIGHT_ARENA` — which the venue on a card does not override. Null when the
   * card decides, which is the game.
   */
  let pinned = null;
  let unsubscribeTrain = null;
  let unsubscribeStruck = null;
  /**
   * Bodies the arena has asked to be laid somewhere after a finish, waiting for
   * the rebound off the surface to play out first. A body moved *into* a bar
   * while its rebound is still being walked would be shoved back out through
   * the front of it and hit it again — a second blow on somebody already out.
   */
  const settling = new Map();
  /** How fast each body in a pit is falling, metres per second. */
  const falling = new Map();

  /**
   * Back to standing height, and nothing pending. On every new round and every
   * swap: a fighter who went off a roof in the last one is on their mark in
   * this one, and the mark is on the floor.
   */
  function standEveryoneUp() {
    settling.clear();
    falling.clear();
    for (const combatant of fight?.combatants ?? []) {
      if (combatant.position && combatant.position.y !== 0) combatant.position.y = 0;
    }
  }

  const unsubscribePhase =
    match?.onPhase?.(({ to }) => {
      if (to !== 'intro') return;
      standEveryoneUp();
      current?.reset?.();
    }) ?? null;

  /**
   * Puts arena `id` on screen.
   *
   * `quiet` is for a swap **inside the intro** — a card that is fought
   * somewhere else, booked while the round is already on its marks and held
   * by a screen. A full swap resets the round, which fires the intro again
   * (and the booking with it) and drops the hold; a quiet one stands the
   * fighters on the new marks and leaves the round where it was. Outside the
   * intro a quiet swap is a full one, because only the intro has marks to
   * stand on.
   */
  function swap(id, { quiet = false } = {}) {
    const entry = findArena(id);
    if (!entry) {
      onWarn?.(`no arena called "${id}"`);
      return current;
    }
    if (current?.id === entry.id) return current;

    // Off first, and completely. A disposed arena that is still in the scene is
    // a set of dead materials being rendered every frame.
    unsubscribeTrain?.();
    unsubscribeTrain = null;
    unsubscribeStruck?.();
    unsubscribeStruck = null;
    standEveryoneUp();
    if (current) {
      stage?.scene.remove(current.object3D);
      current.dispose?.();
    }

    const arena = entry.create(undefined, { onWarn, crowd });
    current = arena;
    stage?.scene.add(arena.object3D);
    // An arena either brings its own floor and lights or borrows the stage's.
    stage?.showGround?.(arena.ground !== false);
    stage?.showLights?.(!arena.lit);

    fight?.setSurfaces(arena.surfaces);
    fight?.setPit(arena.pit, {
      bounds: arena.bounds,
      // Closed over *this* arena, so a swap cannot leave the pit asking a train
      // that is no longer in the scene whether it is in the station.
      lethal: (at) => trainOver(arena.train, at),
    });

    // A moving hard surface joins and leaves the collision with its own phase.
    unsubscribeTrain =
      arena.train?.onChange(({ present }) => {
        if (present) fight?.addSurface(arena.train.surface);
        else fight?.removeSurface(arena.train.surface);
      }) ?? null;

    // The surfaces it owns, struck. Subscribed per arena and closed over this
    // one, for the same reason as the train: a disposed garage that still
    // heard about blows would dent cars nobody can see.
    if (arena.onStruck) {
      unsubscribeStruck =
        fight?.onSurface?.((event) => {
          const answer = arena.onStruck(event);
          if (!answer?.rest) return;
          const body = fight.find?.(event.fighter);
          if (!body) return;
          settling.set(body, {
            rest: answer.rest,
            // Over something on the way, like a parapet, rather than through it.
            arc: answer.arc ?? 0,
            wait: answer.wait ?? SETTLE.wait,
            move: SETTLE.move,
            spent: 0,
            from: null,
          });
        }) ?? null;
    }

    for (const locomotor of locomotors) {
      locomotor?.locomotion?.setBounds(arena.bounds);
      // And the room itself, for a driver that reads one (step 61). Here rather
      // than at the call site for the same reason the bounds are: a swap that
      // re-floors everybody and leaves one of them aiming at the last arena's
      // pillars is the four-of-the-five failure this whole function exists to
      // prevent. A control layer with a person behind it has no `setArena` and
      // does not need one.
      locomotor?.setArena?.(arena);
    }

    if (arena.spawns?.length) {
      match?.setStances(arena.spawns.map((spawn) => [spawn.x, spawn.z]));
    }
    // Back to the marks. The fighters were standing on the last arena's floor,
    // and on a platform that is not a figure of speech.
    if (quiet && match?.phase === 'intro' && match.toMarks) match.toMarks();
    else match?.reset();

    onChange?.(arena);
    return arena;
  }

  /**
   * An arena chosen **by hand**: loaded, and pinned, so the next card's venue
   * does not take it away again. The overlay's buttons and every harness come
   * through here. `unpin` hands the choice back to the cards.
   */
  function load(id) {
    if (!findArena(id)) {
      onWarn?.(`no arena called "${id}"`);
      return current;
    }
    pinned = id;
    return swap(id);
  }

  /**
   * The arena a card is fought in, which is how the game itself chooses one.
   * Defers to a pin, and swaps quietly: see `swap`.
   */
  function visit(id) {
    if (pinned || !id) return current;
    return swap(id, { quiet: true });
  }

  /** Bodies the arena asked to be laid somewhere, eased there once the rebound is done. */
  function settle(dt) {
    for (const [body, job] of settling) {
      if (job.wait > 0) {
        job.wait -= dt;
        continue;
      }
      job.from ??= { x: body.position.x, z: body.position.z };
      job.spent = Math.min(job.move, job.spent + dt);
      const t = job.spent / job.move;
      const eased = t * t * (3 - 2 * t);
      body.position.x = job.from.x + (job.rest.x - job.from.x) * eased;
      body.position.z = job.from.z + (job.rest.z - job.from.z) * eased;
      // Exactly on the floor at the end: sin(pi) is not quite zero, and a body
      // a hair above the floor is a body a test cannot call landed.
      if (job.arc > 0) body.position.y = t >= 1 ? 0 : Math.sin(Math.PI * t) * job.arc;
      if (t >= 1) settling.delete(body);
    }
  }

  /**
   * A body left in a pit falls to the bottom of it.
   *
   * Only a body that is out stays in one — anyone who survived the fall has
   * already been put back on the floor by `fallIntoPit` in the same frame — so
   * this is the picture of a finish, and nothing it does reaches a rule. Before
   * it, a fighter finished in the subway's track lay at platform height in
   * mid-air over the rails; off a roof, that would be lying on the sky.
   */
  function fall(dt) {
    const pit = current?.pit;
    for (const body of fight?.combatants ?? []) {
      const position = body.position;
      if (!position || settling.has(body)) continue;
      if (!pit || !inPit(position, pit)) {
        if (falling.has(body)) {
          falling.delete(body);
          position.y = 0;
        }
        continue;
      }
      const floor = Number.isFinite(pit.floor) ? pit.floor : 0;
      const speed = (falling.get(body) ?? 0) + SETTLE.gravity * dt;
      position.y = Math.max(floor, position.y - speed * dt);
      falling.set(body, position.y > floor ? speed : 0);
    }
  }

  return {
    load,
    visit,
    /** The hand-picked arena, or null when the cards decide. */
    get pinned() {
      return pinned;
    },
    /** Back to the cards: the next booking loads wherever it is fought. */
    unpin() {
      pinned = null;
      return current;
    },
    get current() {
      return current;
    },
    get id() {
      return current?.id ?? null;
    },
    ids: arenaIds,
    list: () => ARENAS,

    /** One frame of whatever this arena has that moves. */
    update(dt) {
      current?.update?.(dt);
      if (!(dt > 0)) return;
      settle(dt);
      fall(dt);
    },

    dispose() {
      unsubscribeTrain?.();
      unsubscribeStruck?.();
      unsubscribePhase?.();
      if (current) {
        stage?.scene.remove(current.object3D);
        current.dispose?.();
      }
      current = null;
    },
  };
}
